/**
 * Store Availability Tabs Module
 * Adapted from Dawn theme's VariantPickerTabs for 6ixty8ight.
 *
 * Fetches per-variant fulfilment data ONCE per PDP load via a single product-level
 * Storefront GraphQL query, then:
 *   - builds an availabilityMap[numericVariantId] = { online, instore, stores }
 *     used to make the size/color grid tab-aware (Online vs In-Store), and
 *   - powers the In-Store store list from the same cached data (no per-variant refetch).
 *
 * See dev plan "Online / In-Store Variant Availability on PDP" for the availability logic.
 */
class StoreAvailabilityTabs {
  constructor(wrapper) {
    if (!wrapper) return;
    this.wrapper = wrapper;
    this.productHandle = wrapper.dataset.productHandle || '';
    this.storefrontToken = wrapper.dataset.storefrontToken;
    this.shopDomain = window.Shopify?.shop || window.location.hostname;
    this.lowStockThreshold = parseInt(wrapper.dataset.lowStockThreshold) || 2;
    this.runningLowThreshold = parseInt(wrapper.dataset.runningLowThreshold) || 5;
    this.iconInStock = wrapper.dataset.iconInStock || '';
    this.iconOutOfStock = wrapper.dataset.iconOutOfStock || '';
    this.labelInStock = wrapper.dataset.labelInStock || 'In Stock';
    this.labelLowStock = wrapper.dataset.labelLowStock || 'Low in Stock';
    this.labelOutOfStock = wrapper.dataset.labelOutOfStock || 'Out of Stock';
    this.labelNoStores = wrapper.dataset.labelNoStores || 'No store locations available.';
    this.labelNoData = wrapper.dataset.labelNoData || 'No store availability data.';
    this.labelLoadError = wrapper.dataset.labelLoadError || 'Unable to load store availability.';

    // Fulfilment config (§5 of the dev plan)
    this.shipIds = new Set(
      (wrapper.dataset.shippingLocationIds || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    );
    this.pickupCounted = wrapper.dataset.pickupCounted === 'true';

    this.tabs = document.querySelectorAll('.variant-tab');
    this.instoreContent = wrapper.querySelector('.tab-content-instore');
    this.storeList = wrapper.querySelector('.store-list');
    this.storeSearch = wrapper.querySelector('.store-search-input');
    this.loadingEl = wrapper.querySelector('.store-locator-loading');

    this.currentVariantId = null;
    this.storesData = [];
    // availabilityMap[numericVariantId] = { online, instore, stores }
    this.availabilityMap = {};
    this._loaded = false;

    this.setupTabs();
    this.setupStoreSearch();
    this.setupVariantListener();

    // Fetch all variants' fulfilment data once on init.
    this.loadFulfillmentData();
  }

  setupTabs() {
    this.tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.tab;

        this.tabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');

        // Drive the Vue app's fulfilment mode so the option grid re-slashes tab-aware.
        if (window.productFormVue?.setFulfillmentMode) {
          window.productFormVue.setFulfillmentMode(target);
        }

        const lowStockEl = document.querySelector('.pdp-low-stock');
        const textBelowAtcInstore = document.querySelector('.text-below-atc-instore');
        if (target === 'in-store') {
          this.instoreContent.style.display = '';
          this.showStoresForVariant();
          if (lowStockEl) lowStockEl.classList.add('hide-m');
          if (textBelowAtcInstore) textBelowAtcInstore.style.display = '';
        } else {
          this.instoreContent.style.display = 'none';
          if (lowStockEl && lowStockEl.dataset.hasMessage === 'true') {
            lowStockEl.classList.remove('hide-m');
          }
          if (textBelowAtcInstore) textBelowAtcInstore.style.display = 'none';
        }
      });
    });
  }

  setupStoreSearch() {
    if (!this.storeSearch) return;
    let debounceTimer;
    this.storeSearch.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.filterStores(this.storeSearch.value.trim());
      }, 200);
    });

    const searchBtn = this.wrapper.querySelector('.store-search-button');
    if (searchBtn) {
      searchBtn.addEventListener('click', () => {
        this.filterStores(this.storeSearch.value.trim());
      });
    }
  }

  setupVariantListener() {
    this._variantListener = () => {
      // Re-render the store list for the newly selected variant (cached data, no refetch).
      if (this.instoreContent && this.instoreContent.style.display !== 'none') {
        this.showStoresForVariant();
      }
    };
    window.addEventListener('pdp:variantChanged', this._variantListener);
  }

  destroy() {
    if (this._variantListener) {
      window.removeEventListener('pdp:variantChanged', this._variantListener);
    }
  }

  getVariantId() {
    if (window.productFormVue?.variantId) return String(window.productFormVue.variantId);
    const atcBtn = document.getElementById('atc-btn');
    if (atcBtn) return atcBtn.dataset.variantId;
    return null;
  }

  // GID -> numeric id ("gid://shopify/ProductVariant/123" -> "123")
  numericId(gid) {
    return String(gid || '').split('/').pop();
  }

  // Backorder flag for a variant, reusing the Vue app's variantList (aggregate policy data).
  isBackorder(numericVariantId) {
    const vue = window.productFormVue;
    if (vue?.getVariantListEntryById) {
      const entry = vue.getVariantListEntryById(numericVariantId);
      return !!(entry && entry.management === 'shopify' && entry.policy === 'continue');
    }
    return false;
  }

  // ---- Data load ----------------------------------------------------------

  async loadFulfillmentData() {
    // Reuse DOM-cached data if it belongs to the current product (survives Vue re-mounts,
    // avoids a duplicate fetch when this module re-inits without a variety swap).
    const cached = this.readCachedData();
    if (cached && cached.handle === this.productHandle && cached.map) {
      this.availabilityMap = cached.map;
      this._loaded = true;
      this.applyToVue();
      this.showStoresForVariant();
      return;
    }

    if (!this.productHandle || !this.storefrontToken) return;

    if (this.loadingEl) this.loadingEl.style.display = '';

    try {
      const query = `
        query getProductAvailability($handle: String!) {
          product(handle: $handle) {
            variants(first: 250) {
              nodes {
                id
                availableForSale
                quantityAvailable
                storeAvailability(first: 250) {
                  nodes {
                    available
                    quantityAvailable
                    location {
                      id
                      name
                      address {
                        address1
                        city
                        province
                        provinceCode
                        zip
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const response = await fetch(`https://${this.shopDomain}/api/2024-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken
        },
        body: JSON.stringify({ query, variables: { handle: this.productHandle } })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        throw new Error('GraphQL query failed');
      }

      const variants = result.data?.product?.variants?.nodes || [];
      if (variants.length === 250) {
        console.warn('[fulfillment] Variant connection hit the 250 cap; some variants may be missing. Add cursor pagination if this product exceeds 250 variants.');
      }

      this.availabilityMap = this.buildAvailabilityMap(variants);
      this._loaded = true;
      this.cacheToDom();
      this.applyToVue();
      this.showStoresForVariant();
    } catch (e) {
      // Graceful fallback: leave availabilityMap empty so the grid keeps its current
      // aggregate-based behaviour and the PDP stays usable.
      console.error('Store availability fetch error:', e);
      if (this.storeList) {
        this.storeList.innerHTML = `<div class="store-no-results p3 c-grey">${this.labelLoadError}</div>`;
      }
    } finally {
      if (this.loadingEl) this.loadingEl.style.display = 'none';
    }
  }

  buildAvailabilityMap(variants) {
    const map = {};
    let driftWarned = false;

    variants.forEach(variant => {
      const numId = this.numericId(variant.id);
      const nodes = variant.storeAvailability?.nodes || [];
      const Q = n => Math.max(0, n.quantityAvailable ?? 0);

      let shipPickupQty = 0; // stock at shipping locations (A) — exact, per-location
      let pickupOnlyQty = 0; // stock at pickup-only locations (B) — exact, per-location
      nodes.forEach(n => {
        const id = this.numericId(n.location?.id);
        if (this.shipIds.has(id)) shipPickupQty += Q(n);
        else pickupOnlyQty += Q(n);
      });

      const qa = variant.quantityAvailable; // may be null (untracked)
      const backorder = this.isBackorder(numId);

      // ── IN-STORE (always exact — pickup data is fully per-location) ──
      const instore = nodes.some(n => Q(n) > 0 || n.available);

      // ── ONLINE ──
      let online;
      if (this.shipIds.size === 0) {
        // Not configured: fall back to aggregate sellability so Online is never wrongly
        // slashed. In-Store still works from per-location pickup data below.
        online = !!variant.availableForSale;
      } else if (qa == null) {
        online = !!variant.availableForSale; // untracked inventory
      } else if (backorder) {
        online = true; // sellable online via backorder
      } else if (shipPickupQty > 0) {
        online = true; // A ships & has stock → EXACT
      } else {
        // A empty: only a ship-only warehouse (W) could ship — the one approximate line.
        const warehouseQty = qa - (this.pickupCounted ? pickupOnlyQty : 0);
        online = warehouseQty > 0;
      }

      // Drift guard (§5.3): qa should be >= sum of per-location pickup stock when
      // pickup-only stock is counted in the online channel.
      if (!driftWarned && this.pickupCounted && qa != null && qa < shipPickupQty + pickupOnlyQty) {
        console.warn('[fulfillment] Inventory drift: quantityAvailable < per-location stock while "pickup counted in online" is enabled. Re-run the pickup-only test / check location config.');
        driftWarned = true;
      }

      map[numId] = { online, instore, stores: this.buildStores(nodes) };
    });

    return map;
  }

  buildStores(nodes) {
    const stores = nodes.map(node => {
      const { quantityAvailable, available, location } = node;
      const qty = Math.max(0, quantityAvailable || 0);

      let stockStatus, stockLabel;
      if (qty > this.runningLowThreshold) {
        stockStatus = 'in-stock';
        stockLabel = this.labelInStock;
      } else if (qty > this.lowStockThreshold) {
        stockStatus = 'running-low';
        stockLabel = this.labelLowStock;
      } else if (qty > 0 || available) {
        stockStatus = 'low-stock';
        stockLabel = this.labelLowStock;
      } else {
        stockStatus = 'out-of-stock';
        stockLabel = this.labelOutOfStock;
      }

      const addr = location?.address || {};
      const addressParts = [addr.address1, addr.city, addr.provinceCode || addr.province].filter(Boolean);

      return {
        name: location?.name || '',
        qty,
        stockStatus,
        stockLabel,
        addressText: addressParts.join(', ')
      };
    });

    stores.sort((a, b) => b.qty - a.qty);
    return stores;
  }

  // Push the map into the Vue app so the option grid becomes tab-aware.
  //
  // Guarded by handle: applyFulfillmentData() re-runs handleUpdatedOptions(), which calls
  // setVariant() and dispatches pdp:productDataUpdated — that re-inits this module. Applying
  // only once per product handle turns that potential infinite loop into a single bounce.
  applyToVue() {
    window._pdpFulfillmentMap = this.availabilityMap;
    if (!window.productFormVue?.applyFulfillmentData) return; // Vue not ready; mounted() pulls it
    if (window._pdpFulfillmentAppliedHandle === this.productHandle) return; // already applied
    window._pdpFulfillmentAppliedHandle = this.productHandle;
    window.productFormVue.applyFulfillmentData(this.availabilityMap);
  }

  // ---- DOM cache ----------------------------------------------------------

  cacheToDom() {
    try {
      let el = document.getElementById('pdp-fulfillment-data');
      if (!el) {
        el = document.createElement('script');
        el.type = 'application/json';
        el.id = 'pdp-fulfillment-data';
        document.body.appendChild(el);
      }
      el.textContent = JSON.stringify({ handle: this.productHandle, map: this.availabilityMap });
    } catch (e) {
      /* non-fatal */
    }
  }

  readCachedData() {
    const el = document.getElementById('pdp-fulfillment-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return null;
    }
  }

  // ---- Store list ---------------------------------------------------------

  showStoresForVariant(explicitVariantId) {
    if (!this.storeList) return;
    const variantId = explicitVariantId || this.getVariantId();
    if (!variantId) return;

    if (!this._loaded) {
      // Data still loading — the load flow will re-render when it resolves.
      return;
    }

    this.currentVariantId = variantId;
    const entry = this.availabilityMap[this.numericId(variantId)];
    this.storesData = (entry && entry.stores) ? entry.stores : [];

    if (!this.storesData.length) {
      this.storeList.innerHTML = `<div class="store-no-results p3 c-grey">${this.labelNoStores}</div>`;
      return;
    }
    // Reset any active search filter view for the new variant.
    if (this.storeSearch) this.storeSearch.value = '';
    this.renderStores(this.storesData);
  }

  renderStores(stores) {
    if (!this.storeList) return;

    if (!stores.length) {
      this.storeList.innerHTML = `<div class="store-no-results p3 c-grey">${this.labelNoData}</div>`;
      return;
    }

    const checkSvg = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L4.5 8.5L9.5 3.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    const minusSvg = `<svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1.5 4H6.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/></svg>`;
    const chevronSvg = `<svg class="store-chevron" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 6L8.00065 10L12 6" stroke="#2D3232" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    const inStockIcon = this.iconInStock || checkSvg;
    const outOfStockIcon = this.iconOutOfStock || minusSvg;

    const items = stores.map((store, index) => {
      const isOos = store.stockStatus === 'out-of-stock';
      const statusIcon = isOos ? outOfStockIcon : inStockIcon;
      const circleClass = `store-status-circle store-status-circle--${store.stockStatus}`;
      const expandedClass = index === 0 && !isOos ? 'expanded' : '';

      return `<div class="store-item ${expandedClass}" data-store-name="${this.escapeAttr(store.name)}">
        <div class="store-row">
          <div class="store-row-left">
            <span class="store-name p2 bold-600">${this.escapeHtml(store.name)}</span>
            ${chevronSvg}
          </div>
          <div class="store-status">
            <div class="${circleClass}">${statusIcon}</div>
            <span class="p3 store-stock-text">${this.escapeHtml(store.stockLabel)}</span>
          </div>
        </div>
        <div class="store-address-detail p3 c-grey">${this.escapeHtml(store.addressText)}</div>
      </div>`;
    });

    this.storeList.innerHTML = `<div class="store-listing">${items.join('<div class="store-item-divider"></div>')}</div>`;

    this.storeList.querySelectorAll('.store-row').forEach(row => {
      row.addEventListener('click', () => {
        row.closest('.store-item').classList.toggle('expanded');
      });
    });
  }

  filterStores(query) {
    if (!query) {
      this.renderStores(this.storesData);
      return;
    }
    const q = query.toLowerCase();
    const filtered = this.storesData.filter(s =>
      s.name.toLowerCase().includes(q) || s.addressText.toLowerCase().includes(q)
    );
    this.renderStores(filtered);
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  static init() {
    const wrapper = document.querySelector('.store-availability-tabs');
    if (wrapper) {
      window._storeAvailabilityTabs?.destroy?.();
      window._storeAvailabilityTabs = new StoreAvailabilityTabs(wrapper);
    }
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', StoreAvailabilityTabs.init);
} else {
  StoreAvailabilityTabs.init();
}

// Re-init after AJAX section swaps (variety change). The DOM cache is keyed by handle,
// so a new variety refetches while the same product reuses cached data.
window.addEventListener('pdp:productDataUpdated', () => {
  setTimeout(StoreAvailabilityTabs.init, 50);
});
